/**
 * CredentialsExport — Modal-based form to download institution credentials.
 * Lets the institutional admin (or super admin) reset & export usernames + temporary
 * passwords as Excel + welcome letters PDF + credential cards PDF, packaged in a ZIP.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Download, FileSpreadsheet, FileText, IdCard,
  KeyRound, Loader2, Sparkles, X, History, ChevronDown, ChevronUp,
  FileDown,
} from "lucide-react";

const ROLE_OPTIONS = [
  { key: "student", label: "Alumnos" },
  { key: "teacher", label: "Docentes" },
  { key: "parent", label: "Padres" },
];

export default function CredentialsExport() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form state
  const [roles, setRoles] = useState({ student: true, teacher: true, parent: true });
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [resetPasswords, setResetPasswords] = useState(true);
  const [tempPassword, setTempPassword] = useState("qhub2026");
  const [includeExcel, setIncludeExcel] = useState(true);
  const [includeLetters, setIncludeLetters] = useState(true);
  const [includeCards, setIncludeCards] = useState(true);

  // Audit log state
  const [logOpen, setLogOpen] = useState(false);
  const [logItems, setLogItems] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const fetchLog = async () => {
    setLogLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/institution/credentials/export-log?limit=20`,
        { withCredentials: true }
      );
      setLogItems(data?.items || []);
    } catch (e) {
      console.error("Failed to load credentials export log:", e);
      toast.error("No se pudo cargar el historial");
    } finally {
      setLogLoading(false);
    }
  };

  const downloadLogCsv = async () => {
    try {
      const res = await axios.get(
        `${API}/institution/credentials/export-log.csv`,
        { withCredentials: true, responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(dispo);
      a.download = match ? match[1] : "audit_credenciales.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const count = res.headers["x-records-exported"] || "?";
      toast.success(`CSV exportado: ${count} registros`);
    } catch (e) {
      console.error("Failed to download audit CSV:", e);
      toast.error("No se pudo descargar el CSV de auditoría");
    }
  };

  useEffect(() => {
    if (open && logOpen) fetchLog();
    // Refetch after a successful export to show the new row
  }, [open, logOpen]);

  const submit = async () => {
    const selectedRoles = Object.keys(roles).filter((r) => roles[r]);
    if (selectedRoles.length === 0) {
      toast.error("Selecciona al menos un rol");
      return;
    }
    if (!includeExcel && !includeLetters && !includeCards) {
      toast.error("Selecciona al menos un formato");
      return;
    }
    if (resetPasswords && (tempPassword || "").length < 4) {
      toast.error("La contraseña temporal debe tener al menos 4 caracteres");
      return;
    }
    setLoading(true);
    try {
      const body = {
        roles: selectedRoles,
        grade: grade.trim() || null,
        section: section.trim().toUpperCase() || null,
        reset_passwords: resetPasswords,
        temp_password: tempPassword,
        include_excel: includeExcel,
        include_letters_pdf: includeLetters,
        include_cards_pdf: includeCards,
      };
      const res = await axios.post(`${API}/institution/credentials/export`, body, {
        withCredentials: true,
        responseType: "blob",
      });
      const count = res.headers["x-users-exported"] || "?";
      // Trigger download
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(dispo);
      a.download = match ? match[1] : "credenciales.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Listo: ${count} usuarios exportados${resetPasswords ? " (contraseñas reseteadas)" : ""}`);
      // Refresh log if it's open
      if (logOpen) fetchLog();
      setOpen(false);
    } catch (e) {
      // Try to parse blob error
      let msg = "Error al exportar credenciales";
      if (e.response?.data) {
        try {
          const text = await e.response.data.text?.();
          if (text) {
            try { msg = JSON.parse(text).detail || msg; } catch { msg = text; }
          }
        } catch { /* keep default */ }
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white hover:opacity-90 gap-2"
        data-testid="credentials-export-btn"
      >
        <Download className="w-4 h-4" />
        Descargar credenciales
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-4"
          onClick={() => !loading && setOpen(false)}
          data-testid="credentials-export-modal"
        >
          <div
            className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl my-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#419cca]/5 to-[#8b5cf6]/5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900">Descargar credenciales</h2>
                  <p className="text-xs text-gray-500">Excel + cartas de bienvenida + fichas, en un ZIP</p>
                </div>
              </div>
              <button
                onClick={() => !loading && setOpen(false)}
                className="text-gray-400 hover:text-gray-700 p-1"
                disabled={loading}
                data-testid="credentials-export-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-6">
              {/* Filters */}
              <section>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Filtros</h3>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-3">
                    <Label className="text-xs text-gray-600">Roles a incluir</Label>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {ROLE_OPTIONS.map((r) => (
                        <button
                          key={r.key}
                          type="button"
                          onClick={() => setRoles((p) => ({ ...p, [r.key]: !p[r.key] }))}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                            roles[r.key]
                              ? "bg-[#419cca] text-white border-[#419cca] shadow-sm"
                              : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                          }`}
                          data-testid={`cred-role-${r.key}`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Los administradores nunca se incluyen, por seguridad.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Grado (opcional)</Label>
                    <Input
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      placeholder="ej: 7"
                      data-testid="cred-grade"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Sección (opcional)</Label>
                    <Input
                      value={section}
                      onChange={(e) => setSection(e.target.value.toUpperCase())}
                      placeholder="ej: A"
                      maxLength={4}
                      data-testid="cred-section"
                    />
                  </div>
                </div>
              </section>

              {/* Password mode */}
              <section>
                <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-amber-500" />
                  Contraseñas
                </h3>
                <div className="space-y-2">
                  <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl border-2 border-amber-200 bg-amber-50">
                    <input
                      type="radio"
                      checked={resetPasswords}
                      onChange={() => setResetPasswords(true)}
                      className="mt-1"
                      data-testid="cred-reset-on"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        Resetear a una contraseña temporal{" "}
                        <span className="text-amber-700">(recomendado para entrega inicial)</span>
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Todos los usuarios filtrados quedarán con esta contraseña hasta que ellos la cambien.
                      </p>
                      <Input
                        value={tempPassword}
                        onChange={(e) => setTempPassword(e.target.value)}
                        disabled={!resetPasswords}
                        className="mt-2 font-mono"
                        data-testid="cred-temp-pwd"
                      />
                    </div>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={!resetPasswords}
                      onChange={() => setResetPasswords(false)}
                      className="mt-1"
                      data-testid="cred-reset-off"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">No resetear contraseñas</p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Para usuarios que ya cambiaron su clave, la columna Contraseña dirá
                        “(ya configurada por el usuario)”.
                      </p>
                    </div>
                  </label>
                </div>
              </section>

              {/* Formats */}
              <section>
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Formatos a incluir</h3>
                <div className="grid sm:grid-cols-3 gap-2">
                  <label className={`p-3 rounded-xl border-2 cursor-pointer flex gap-3 ${
                    includeExcel ? "border-emerald-300 bg-emerald-50" : "border-gray-200"
                  }`}>
                    <input
                      type="checkbox"
                      checked={includeExcel}
                      onChange={(e) => setIncludeExcel(e.target.checked)}
                      className="mt-1"
                      data-testid="cred-excel"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                        <FileSpreadsheet className="w-4 h-4 text-emerald-600" />Excel
                      </p>
                      <p className="text-[11px] text-gray-500">Tabla con todas las credenciales</p>
                    </div>
                  </label>
                  <label className={`p-3 rounded-xl border-2 cursor-pointer flex gap-3 ${
                    includeLetters ? "border-violet-300 bg-violet-50" : "border-gray-200"
                  }`}>
                    <input
                      type="checkbox"
                      checked={includeLetters}
                      onChange={(e) => setIncludeLetters(e.target.checked)}
                      className="mt-1"
                      data-testid="cred-letters"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                        <FileText className="w-4 h-4 text-violet-600" />Cartas
                      </p>
                      <p className="text-[11px] text-gray-500">1 hoja personalizada por usuario</p>
                    </div>
                  </label>
                  <label className={`p-3 rounded-xl border-2 cursor-pointer flex gap-3 ${
                    includeCards ? "border-amber-300 bg-amber-50" : "border-gray-200"
                  }`}>
                    <input
                      type="checkbox"
                      checked={includeCards}
                      onChange={(e) => setIncludeCards(e.target.checked)}
                      className="mt-1"
                      data-testid="cred-cards"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                        <IdCard className="w-4 h-4 text-amber-600" />Fichas
                      </p>
                      <p className="text-[11px] text-gray-500">4 por hoja, ideal alumnos</p>
                    </div>
                  </label>
                </div>
              </section>

              {/* Audit log (collapsible) */}
              <section className="border-t border-gray-100 pt-4 -mx-6 px-6">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setLogOpen((v) => !v)}
                    className="flex-1 flex items-center justify-between text-left"
                    data-testid="cred-log-toggle"
                  >
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <History className="w-4 h-4 text-gray-500" />
                      Historial de descargas
                      <span className="text-xs font-normal text-gray-400">
                        (auditoría · auto-purga a los 18 meses)
                      </span>
                    </h3>
                    {logOpen
                      ? <ChevronUp className="w-4 h-4 text-gray-500" />
                      : <ChevronDown className="w-4 h-4 text-gray-500" />}
                  </button>
                  {logOpen && logItems.length > 0 && (
                    <button
                      type="button"
                      onClick={downloadLogCsv}
                      className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-700"
                      data-testid="cred-log-csv"
                      title="Descargar CSV con todos los registros (hasta 10.000)"
                    >
                      <FileDown className="w-3.5 h-3.5" />
                      CSV
                    </button>
                  )}
                </div>
                {logOpen && (
                  <div className="mt-3 max-h-56 overflow-y-auto border border-gray-100 rounded-lg" data-testid="cred-log-panel">
                    {logLoading ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                        Cargando…
                      </div>
                    ) : logItems.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-400">
                        Aún no hay descargas registradas.
                      </div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr className="text-gray-600">
                            <th className="text-left px-3 py-2 font-semibold">Fecha</th>
                            <th className="text-left px-3 py-2 font-semibold">Quién</th>
                            <th className="text-left px-3 py-2 font-semibold">Filtros</th>
                            <th className="text-left px-3 py-2 font-semibold">Reset</th>
                            <th className="text-right px-3 py-2 font-semibold">Usuarios</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logItems.map((it) => (
                            <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`cred-log-row-${it.id}`}>
                              <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                {it.created_at
                                  ? new Date(it.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })
                                  : "—"}
                              </td>
                              <td className="px-3 py-2 text-gray-700">{it.performed_by_name || it.performed_by_email}</td>
                              <td className="px-3 py-2 text-gray-600">
                                {(it.filters?.roles || []).join(", ") || "—"}
                                {it.filters?.grade ? ` · ${it.filters.grade}` : ""}
                                {it.filters?.section ? `${it.filters.section}` : ""}
                              </td>
                              <td className="px-3 py-2">
                                {it.reset_passwords
                                  ? <span className="text-amber-700 font-semibold">Sí</span>
                                  : <span className="text-gray-400">No</span>}
                              </td>
                              <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900">{it.users_exported}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </section>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2 rounded-b-2xl bg-gray-50">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={loading}
                data-testid="cred-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={submit}
                disabled={loading}
                className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white hover:opacity-90"
                data-testid="cred-submit"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando ZIP…</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Generar y descargar</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
